/**
 * `.zwdoc` → HTML for the book builders (#258): PDF and EPUB render it,
 * DOCX walks it. Same markup as the editor's schema `toDOM`, except:
 * comments are dropped, spacers use the builders' `zw-export-spacer`, list
 * items holding one paragraph are tight (`<li>text</li>`), and `raw` blocks
 * (Markdown the converter kept verbatim, e.g. tables) render as Markdown.
 * Alignment and indent stay as inline `text-align` / `margin-left` styles.
 */

import {
  DOMSerializer,
  type DOMOutputSpec,
  type Node as PMNode,
} from "prosemirror-model";
import { withoutAllComments } from "../lib/zwdoc/comments";
import { zwdocSchema } from "../lib/zwdoc/schema";
import { markSceneOpening, type SceneOpening } from "./chapterOpening";
import { renderMarkdown } from "./exportAssets";

const base = DOMSerializer.fromSchema(zwdocSchema);

const exportSerializer = new DOMSerializer(
  {
    ...base.nodes,
    spacer: (node): DOMOutputSpec => [
      "div",
      {
        class: "zw-export-spacer",
        style: `height:${String(node.attrs.height)}`,
        "aria-hidden": "true",
      },
    ],
    raw: (node): DOMOutputSpec => {
      const wrapper = document.createElement("div");
      wrapper.className = "zw-export-raw";
      wrapper.innerHTML = renderMarkdown(String(node.attrs.markdown));
      return wrapper;
    },
  },
  base.marks,
);

/** `<li><p>text</p></li>` → `<li>text</li>` when the item has one paragraph. */
function tightenListItems(root: HTMLElement): void {
  for (const li of root.querySelectorAll("li")) {
    const paragraphs = Array.from(li.children).filter(
      (child) => child.tagName === "P",
    );
    const first = paragraphs[0];
    if (paragraphs.length !== 1 || first !== li.firstElementChild) continue;
    first.replaceWith(...Array.from(first.childNodes));
  }
}

/** Top-level blocks as HTML, one per line. `chapterOpening` styles the first
 * paragraph (`PLAIN_SCENE_OPENING` for every scene but a chapter's first). */
export function zwdocToExportHtml(
  doc: PMNode,
  chapterOpening: SceneOpening,
): string {
  const clean = withoutAllComments(doc);
  const container = document.createElement("div");
  container.appendChild(exportSerializer.serializeFragment(clean.content));
  tightenListItems(container);
  // Raw Markdown renders to its own blocks; drop the serializer's wrapper.
  for (const raw of container.querySelectorAll(".zw-export-raw")) {
    raw.replaceWith(...Array.from(raw.childNodes));
  }
  markSceneOpening(container, chapterOpening);
  return Array.from(container.children, (el) => el.outerHTML.trim())
    .filter((html) => html !== "<p></p>")
    .join("\n");
}
